import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Send, Users, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../services/api';

const CommunicationModule = () => {
  const [users, setUsers] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [commType, setCommType] = useState('InApp');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const commsRes = await api.communications.getAll();
      // Handle both array and {data: []} response formats
      const commsData = commsRes.data?.data || commsRes.data || [];
      setCommunications(Array.isArray(commsData) ? commsData : []);
      
      setUsers([
        { id: 'user-depthead-001', name: 'Dr. Priya Sharma', role: 'DeptHead', email: 'depthead@water.gov.in', phone: '+91-9876543211' },
        { id: 'user-field-001', name: 'Rajesh Verma', role: 'FieldOfficer', email: 'field1@water.gov.in', phone: '+91-9876543212' },
        { id: 'user-field-002', name: 'Amit Kumar', role: 'FieldOfficer', email: 'field2@water.gov.in', phone: '+91-9876543213' }
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setCommunications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedUser || !message) {
      alert('Please select a recipient and enter a message');
      return;
    }

    try {
      await api.communications.create({
        sent_to: selectedUser.id,
        type: commType,
        message_text: message
      });
      
      alert(`Message sent to ${selectedUser.name} via ${commType}!`);
      setMessage('');
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading communications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className={`w-full p-3 rounded-lg text-left transition-all ${
                    selectedUser?.id === user.id
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-slate-800 border-2 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center font-bold">
                      {user.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{user.name}</p>
                      <p className="text-xs text-gray-400">{user.role}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare size={20} />
              Send Message
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedUser ? (
              <div className="space-y-4">
                <div className="bg-slate-800 p-4 rounded-lg">
                  <p className="text-sm text-gray-400">Sending to:</p>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail size={12} />
                      {selectedUser.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone size={12} />
                      {selectedUser.phone}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Communication Type</label>
                  <div className="flex gap-2">
                    {['InApp', 'Email', 'SMS', 'WhatsApp'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setCommType(type)}
                        className={`px-4 py-2 rounded text-sm ${
                          commType === type
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here..."
                    className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2 min-h-[150px]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Quick Templates</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setMessage('Please investigate the water quality issue in your area and report back.')}
                      className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded text-xs text-left"
                    >
                      Investigation Request
                    </button>
                    <button
                      onClick={() => setMessage('Critical alert in your zone. Please take immediate action.')}
                      className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded text-xs text-left"
                    >
                      Critical Alert
                    </button>
                    <button
                      onClick={() => setMessage('Please submit your daily report by end of day.')}
                      className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded text-xs text-left"
                    >
                      Report Reminder
                    </button>
                    <button
                      onClick={() => setMessage('Schedule a meeting to discuss the recent water loss issues.')}
                      className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded text-xs text-left"
                    >
                      Meeting Request
                    </button>
                  </div>
                </div>

                <Button onClick={handleSendMessage} className="w-full">
                  <Send size={16} className="mr-2" />
                  Send Message
                </Button>
              </div>
            ) : (
              <p className="text-gray-400 text-center py-12">Select a team member to send a message</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle>Recent Communications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {communications.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No communications yet</p>
            ) : (
              communications.slice(0, 10).map((comm) => (
                <div key={comm.id} className="bg-slate-800 p-4 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          comm.type === 'InApp' ? 'bg-blue-500/20 text-blue-400' :
                          comm.type === 'Email' ? 'bg-green-500/20 text-green-400' :
                          comm.type === 'SMS' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {comm.type}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          comm.status === 'Delivered' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {comm.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 mb-2">{comm.message_text}</p>
                      <p className="text-xs text-gray-500">
                        Sent: {new Date(comm.sent_at).toLocaleString()} | To: {comm.sent_to}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationModule;
